<template>
  <tr
    :class="[
      'ppm-row',
      row.node_type === 'work'      ? 'ppm-row--work'      : '',
      row.node_type === 'floor'     ? 'ppm-row--floor'     : '',
      row.node_type === 'floor' && !row.usp_level_guid ? 'ppm-row--unlinked' : '',
      row.node_type === 'block'     ? 'ppm-row--block'     : '',
      row.node_type === 'group'     ? 'ppm-row--group'     : '',
      row._depth === 0              ? 'ppm-row--root'      : '',
      row.hasWorks === false         ? 'ppm-row--no-works'  : '',
      row._vIdx % 2 === 1           ? 'ppm-row--even'      : '',
    ]"
  >
    <!-- ─── Узел (sticky) ─── -->
    <td
      class="td-node sticky-col"
      :style="{ width: colWidths.node + 'px', minWidth: colWidths.node + 'px', maxWidth: colWidths.node + 'px' }"
    >
      <div
        class="node-cell"
        :class="{ 'node-cell--floor': row.node_type === 'floor' }"
        :style="{ paddingLeft: (row._depth * 12 + 4) + 'px' }"
      >
        <template v-if="row._hasChildren || floorLoadingIds[row.id] || blockLoadingIds[row.id]">
          <q-spinner
            v-if="floorLoadingIds[row.id] || blockLoadingIds[row.id]"
            size="12px" color="grey-5"
            class="expand-spacer-spinner"
          />
          <button
            v-else
            class="expand-btn"
            @click.stop="$emit('toggle-expand', row.id)"
            :title="expandedSet.has(row.id) ? 'Свернуть' : 'Развернуть'"
          >
            <span class="expand-icon">{{ expandedSet.has(row.id) ? '▾' : '▸' }}</span>
          </button>
        </template>
        <span v-else class="expand-spacer" />

        <q-icon
          v-if="row.hasWarning"
          name="warning_amber"
          size="14px"
          class="node-warning-icon"
          title="Нет связи с УСП"
        />

        <q-icon
          v-if="row.node_type === 'floor'"
          name="layers"
          size="13px"
          class="node-icon--floor"
        />
        <q-icon
          v-if="row.node_type === 'group'"
          name="folder"
          size="12px"
          class="node-icon--group"
        />
        <q-icon
          v-else-if="row.node_type === 'work'"
          name="build"
          size="12px"
          :class="row.usp_work_guid ? 'node-icon--work' : 'node-icon--work node-icon--work-no-usp'"
          :title="!row.usp_work_guid ? 'Нет связи в УСП' : ''"
        />

        <div
          class="node-label-wrap"
          :class="{ 'node-label-wrap--expandable': row._hasChildren }"
          @dblclick.stop="row._hasChildren ? $emit('toggle-expand', row.id) : null"
        >
          <div class="node-label-row">
            <span
              class="node-label"
              :class="{
                'node-label--root':  row._depth === 0,
                'node-label--block': row.node_type === 'block',
                'node-label--floor': row.node_type === 'floor',
                'node-label--group': row.node_type === 'group',
                'node-label--work':  row.node_type === 'work',
              }"
            >
              {{ (showFullNames && row.fullLabel) ? row.fullLabel : row.label }}
            </span>

            <span
              v-if="row.node_type === 'block' && !row.usp_block_guid"
              class="unlinked-badge"
              title="Блок не имеет связи с УСП — интеграция невозможна"
            >
              <q-icon name="link_off" size="12px" />
              Блок не связан с УСП
            </span>
            <span
              v-if="row.node_type === 'floor' && !row.usp_level_guid"
              class="unlinked-badge"
              title="Этаж не имеет связи с УСП — интеграция невозможна"
            >
              <q-icon name="link_off" size="12px" />
              Этаж не связан с УСП
            </span>
            <span v-if="row.hasWorks === false" class="no-works-badge">
              <q-icon name="block" size="11px" />
              нет работ
            </span>
            <span
              v-if="row.node_type === 'group' && !row.usp_level_guid"
              class="no-usp-work-badge"
              title="Этаж не связан с УСП — интеграция невозможна"
            >
              <q-icon name="warning_amber" size="11px" />
              Работа УСП не определена
            </span>
            <span
              v-if="row.node_type === 'work' && !row.usp_work_guid"
              class="no-usp-work-badge"
              title="Для работы не удалось определить связь с УСП. Отправка по интеграции невозможна."
            >
              <q-icon name="warning_amber" size="11px" />
              Работа УСП не определена
            </span>
            <span
              v-if="(row.node_type === 'work' || row.node_type === 'group') && isSmetaBlocked"
              class="smeta-blocked-badge"
              :title="'Смета изменена (не подписано ДС). Ввод факта заблокирован.'"
            >
              <q-icon name="lock" size="11px" />
              {{ smetaBlockedLabel }}
            </span>
          </div>

          <!-- Периоды ГПР под именем этажа -->
          <template v-if="row.node_type === 'floor' && row.usp_level_guid">
            <div v-if="gprLoadingGuids[gprKeyForRow(row)]" class="floor-gpr-sub floor-gpr-sub--loading">
              <q-spinner size="9px" color="grey-5" />
            </div>
            <div v-else class="floor-gpr-rows">
              <div class="floor-gpr-row floor-gpr-row--base">
                <span class="floor-gpr-label">Баз.</span>
                <span class="floor-gpr-dates">{{ getGprText(row, 'base') }}</span>
              </div>
              <div class="floor-gpr-row floor-gpr-row--cur">
                <span class="floor-gpr-label">Тек.</span>
                <span class="floor-gpr-dates">{{ getGprText(row, 'current') }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </td>

    <!-- ─── Объём / ед. (только для работ) ─── -->
    <td
      class="td-amount td-ro sticky-col sticky-col--amount"
      :style="{ left: colWidths.node + 'px', width: colWidths.amount + 'px', minWidth: colWidths.amount + 'px', maxWidth: colWidths.amount + 'px' }"
    >
      <div v-if="row.node_type === 'work'" class="cell-ro cell-amount-val">
        <span v-if="row.work_set_amount != null" class="amount-plan-val">{{ fmtNum(row.work_set_amount) }}</span>
        <span v-if="row.unit_name" class="amount-unit">{{ row.unit_name }}</span>
      </div>
      <div v-else class="cell-ro cell-ro--empty"></div>
    </td>

    <!-- ─── Факт ─── -->
    <td
      class="td-amount td-fact td-ro sticky-col sticky-col--fact"
      :style="{ left: (colWidths.node + colWidths.amount) + 'px', width: colWidths.fact + 'px', minWidth: colWidths.fact + 'px', maxWidth: colWidths.fact + 'px' }"
    >
      <div
        v-if="row.node_type === 'work' && row.work_set_amount != null"
        class="cell-ro cell-fact-val cell-fact-clickable"
        @click="$emit('open-fact-history', row)"
      >
        <span
          class="fact-sum"
          :class="{
            'fact-sum--over': cumulativeFactSum > row.work_set_amount,
            'fact-sum--has':  cumulativeFactSum > 0,
          }"
        >{{ fmtNum(cumulativeFactSum) }}</span>
        <span class="fact-pct">
          {{ cumulativeFactPct }}%
        </span>
        <q-btn
          :icon="isWorkComplete() ? 'check_circle' : 'task_alt'"
          flat round dense size="xs"
          :color="isWorkComplete() ? 'green-9' : 'green-7'"
          class="complete-row-btn"
          :class="{ 'complete-row-btn--done': isWorkComplete() }"
          @click.stop="isWorkComplete() ? $emit('uncomplete-work', row) : $emit('complete-work', [row])"
          :title="isWorkComplete() ? 'Снять завершение' : 'Завершить работу'"
        />
      </div>

      <!-- Статус завершённости конструктива + % иерархии -->
      <div
        v-else-if="row.node_type === 'group'"
        class="cell-ro cell-group-status cell-fact-clickable"
        @click="$emit('open-fact-history', row)"
      >
        <q-icon
          v-if="groupCompletionMap[row.id] === 'all'"
          name="check_circle"
          color="green-8"
          size="20px"
          title="Все работы завершены"
        />
        <q-icon
          v-else-if="groupCompletionMap[row.id] === 'partial'"
          name="rule"
          color="orange-7"
          size="18px"
          title="Часть работ завершена"
        />
        <span
          v-if="hierarchyPctMap[row.id] != null"
          class="hierarchy-fact-pct"
          :class="{ 'hierarchy-fact-pct--full': hierarchyPctMap[row.id] >= 100 }"
        >{{ hierarchyPctMap[row.id] }}%</span>
      </div>

      <!-- % выполнения для вышестоящих узлов -->
      <div
        v-else-if="['floor','block','project_type','resident'].includes(row.node_type)"
        class="cell-ro cell-hierarchy-pct cell-fact-clickable"
        @click="$emit('open-fact-history', row)"
      >
        <span
          v-if="hierarchyPctMap[row.id] != null"
          class="hierarchy-fact-pct"
          :class="{ 'hierarchy-fact-pct--full': hierarchyPctMap[row.id] >= 100 }"
        >{{ hierarchyPctMap[row.id] }}%</span>
      </div>

      <div v-else class="cell-ro cell-ro--empty"></div>
    </td>

    <!-- ─── Дни ─── -->
    <template v-if="calendarDates.length">
      <PpmDayCell
        v-for="d in calendarDates"
        :key="'c-' + row.id + '-' + d.key"
        :row="row"
        :date="d"
        :cell-val="getCellVal(d.key)"
        :cell-meta="getCellMeta(d.key)"
        :today-iso="todayIso"
        :is-future-date="isFutureDate"
        :is-complete="isWorkComplete()"
        :cell-class-obj="cellClass(d)"
        :group-completion="groupCompletionMap[row.id]"
        :parent-cell-data="parentCellDataRow[d.key]"
        :is-smeta-blocked="isSmetaBlocked"
        :storno-mode="stornoMode"
        @open-fact-dialog="$emit('open-fact-dialog', row, d)"
        @open-group-fact-dialog="$emit('open-group-fact-dialog', row, d)"
        @delete-fact="$emit('delete-fact', row, d)"
      />
    </template>
    <td v-else class="td-day td-empty" />
  </tr>
</template>

<script>
import PpmDayCell from './PpmDayCell.vue';
import { useFormatters } from '../composables/useFormatters';

export default {
  name: 'PpmTableRow',
  components: { PpmDayCell },

  setup() {
    const { fmtNum, fmtKzt, fmtRange } = useFormatters();
    return { fmtNum, fmtKzt, fmtRange };
  },

  props: {
    row:                { type: Object,  required: true },
    calendarDates:      { type: Array,   required: true },
    expandedSet:        { type: Set,     required: true },
    colWidths:          { type: Object,  required: true },
    todayIso:           { type: String,  default: '' },
    isFutureDate:       { type: Boolean, default: false },
    showFullNames:      { type: Boolean, default: false },
    hierarchyPctMap:    { type: Object,  default: () => ({}) },
    groupCompletionMap: { type: Object,  default: () => ({}) },
    parentCellDataMap:  { type: Object,  default: () => ({}) },
    floorLoadingIds:    { type: Object,  default: () => ({}) },
    blockLoadingIds:    { type: Object,  default: () => ({}) },
    gprLoadingGuids:    { type: Object,  default: () => ({}) },
    gprPeriodsByName:   { type: Object,  default: () => ({}) },
    completedWorks:     { type: Set,     default: () => new Set() },
    cellValuesRow:      { type: Object,  default: () => ({}) },
    cellMetaRow:        { type: Object,  default: () => ({}) },
    cumulativeFactSum:  { type: Number,  default: 0 },
    cumulativeFactPct:  { type: Number,  default: 0 },
    smetaChangeLog:     { type: Object,  default: () => ({}) },
    stornoMode:         { type: Boolean, default: false },
  },

  emits: [
    'toggle-expand',
    'open-fact-dialog',
    'open-group-fact-dialog',
    'open-fact-history',
    'complete-work',
    'uncomplete-work',
    'delete-fact',
  ],

  computed: {
    parentCellDataRow() {
      return this.parentCellDataMap[this.row.id] || {};
    },
    smetaKey() {
      const r = this.row;
      return `${r.floor_map_id}_${r.work_set_id}_${r.work_set_check_group_id || 0}`;
    },
    isSmetaBlocked() {
      if (this.row.node_type === 'work') return !!this.smetaChangeLog[this.smetaKey];
      if (this.row.node_type === 'group') {
        return (this.row.children || []).some((c) => {
          const k = `${c.floor_map_id}_${c.work_set_id}_${c.work_set_check_group_id || 0}`;
          return !!this.smetaChangeLog[k];
        });
      }
      return false;
    },
    smetaBlockedLabel() {
      if (!this.isSmetaBlocked) return '';
      let info = this.smetaChangeLog[this.smetaKey];
      if (!info && this.row.node_type === 'group') {
        const c = (this.row.children || []).find((w) => {
          const k = `${w.floor_map_id}_${w.work_set_id}_${w.work_set_check_group_id || 0}`;
          return !!this.smetaChangeLog[k];
        });
        if (c) info = this.smetaChangeLog[`${c.floor_map_id}_${c.work_set_id}_${c.work_set_check_group_id || 0}`];
      }
      if (info?.status_code === 'added') return 'Добавлен';
      return 'Изменён';
    },
  },

  methods: {
    getCellVal(dateKey) {
      return this.cellValuesRow?.[dateKey] ?? '';
    },

    getCellMeta(dateKey) {
      return this.cellMetaRow?.[dateKey] || {};
    },

    isWorkComplete() {
      return this.completedWorks.has(
        `${this.row.floor_map_id}_${this.row.work_set_id}_${this.row.work_set_check_group_id}`
      );
    },

    gprKeyForRow(row) {
      if (row?.node_type === 'floor' && row.usp_level_guid) return String(row.usp_level_guid);
      return null;
    },

    getGprText(row, kind) {
      const key = this.gprKeyForRow(row) || '';
      const p   = this.gprPeriodsByName?.[key];
      if (!p) return '—';
      if (kind === 'base')    return this.fmtRange(p.base_from, p.base_to);
      if (kind === 'current') return this.fmtRange(p.cur_from,  p.cur_to);
      return '—';
    },

    getGprShort(row, kind) {
      const key = this.gprKeyForRow(row) || '';
      const p   = this.gprPeriodsByName?.[key];
      if (!p) return '—';
      const fmt = (iso) => {
        if (!iso) return null;
        const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return m ? `${m[3]}.${m[2]}` : null;
      };
      const a = kind === 'base' ? fmt(p.base_from) : fmt(p.cur_from);
      const b = kind === 'base' ? fmt(p.base_to)   : fmt(p.cur_to);
      if (!a && !b) return '—';
      if (!b || a === b) return a || '—';
      return `${a}–${b}`;
    },

    cellClass(d) {
      const val   = this.getCellVal(d.key);
      const haVal = val !== '' && val !== null;
      const isDone = this.isWorkComplete();
      if (isDone && !haVal) return { 'cell-click--done': true };
      if (!haVal) return {};
      const meta    = this.getCellMeta(d.key);
      const factNum = parseFloat(val) || 0;
      const planNum = parseFloat(this.row.work_set_amount) || 0;
      const isOver  = planNum > 0 && factNum > planNum;
      return {
        'cell-click--filled': true,
        'cell-click--sent':   !!meta.isSent && !isOver,
        'cell-click--saved':  !meta.isSent  && !isOver,
        'cell-click--over':   isOver,
      };
    },
  },
};
</script>
